import { Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, AlertCircle, X } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import { Checkbox } from '@/components/ui/checkbox'
import logoSecondLane from '@/assets/logoSecondLane.svg'
import logoSecondLaneDark from '@/assets/logoSecondLaneDark.svg'
import checkCircleIcon from '@/assets/check_circle.svg'
import type { SignupScreenBaseProps } from './SignupScreenBase'
import { getPasswordStrength } from './SignupScreenBase'

export const SignupScreenMobile = ({
  formData,
  showPassword,
  showConfirmPassword,
  acceptTerms,
  loading,
  error,
  onFormDataChange,
  onShowPasswordChange,
  onShowConfirmPasswordChange,
  onAcceptTermsChange,
  onClearField,
  onSubmit,
}: SignupScreenBaseProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({
      [e.target.name]: e.target.value
    })
  }

  const passwordStrength = getPasswordStrength(formData.password)

  return (
    <div className="relative min-h-screen bg-white px-6 pb-8 pt-6 dark:bg-[#0d0d0d]">
      <div className="pointer-events-none absolute inset-0 hidden dark:block [background:radial-gradient(680px_420px_at_18%_24%,rgba(255,255,255,0.08),transparent_58%),radial-gradient(420px_280px_at_24%_70%,rgba(255,255,255,0.04),transparent_65%)]" />
      <div className="mx-auto max-w-[402px]">
        <div className="mb-12 flex justify-center">
          <img src={logoSecondLane} alt="SecondLane" className="h-6 w-24 dark:hidden" />
          <img src={logoSecondLaneDark} alt="SecondLane" className="hidden h-6 w-24 dark:block" />
        </div>

        <div className="mb-12 text-center">
          <h1 className="font-plus-jakarta text-[30px] font-medium leading-[40px] tracking-[-0.28px] text-[#2d2923] dark:text-white">
            Create Account
          </h1>
          <p className="font-plus-jakarta text-[16px] font-normal leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(255,255,255,0.64)]">
            Join SecondLane and start trading
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 border border-red-500 bg-red-500/10 px-4 py-3 text-red-500">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-3">
            <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
              Email Address
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              leftElement={<Mail size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
              rightElement={formData.email ? (
                <button
                  type="button"
                  onClick={() => onClearField('email')}
                  className="text-[rgba(45,41,35,0.72)] transition-colors hover:text-[#2d2923] dark:text-[rgba(255,255,255,0.72)] dark:hover:text-white"
                >
                  <X size={18} />
                </button>
              ) : undefined}
              className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] !bg-white !text-[14px] !font-medium !leading-[20px] !text-[#2d2923] placeholder:!text-[rgba(45,41,35,0.72)] dark:!border-[rgba(255,255,255,0.12)] dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-[rgba(255,255,255,0.72)]"
              required
            />
          </div>

          <div className="space-y-3">
            <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
              Password
            </label>
            <Input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              leftElement={<Lock size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
              rightElement={
                <button
                  type="button"
                  onClick={() => onShowPasswordChange(!showPassword)}
                  className="text-[rgba(45,41,35,0.72)] transition-colors hover:text-[#2d2923] dark:text-[rgba(255,255,255,0.72)] dark:hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              }
              className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] !bg-white !text-[14px] !font-medium !leading-[20px] !text-[#2d2923] placeholder:!text-[rgba(45,41,35,0.72)] dark:!border-[rgba(255,255,255,0.12)] dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-[rgba(255,255,255,0.72)]"
              required
            />
            {formData.password && (
              <div className="flex items-center gap-2">
                <div className="flex min-h-px flex-1 items-center gap-1">
                  {[1, 2, 3, 4].map((segment) => (
                    <div
                      key={segment}
                      className={`h-[10px] min-h-px flex-1 ${
                        passwordStrength.level >= segment
                          ? 'bg-[rgba(45,41,35,0.7)] dark:bg-[rgba(255,255,255,0.7)]'
                          : 'border border-[rgba(45,41,35,0.24)] bg-white dark:border-[rgba(255,255,255,0.24)] dark:bg-[#1c1c1c]'
                      }`}
                    />
                  ))}
                </div>
                {passwordStrength.text && (
                  <span className="font-plus-jakarta text-[12px] font-semibold leading-[16px] text-[#2d2923] dark:text-white">
                    {passwordStrength.text}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)]">
              Confirm Password
            </label>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              leftElement={<Lock size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
              rightElement={
                <button
                  type="button"
                  onClick={() => onShowConfirmPasswordChange(!showConfirmPassword)}
                  className="text-[rgba(45,41,35,0.72)] transition-colors hover:text-[#2d2923] dark:text-[rgba(255,255,255,0.72)] dark:hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              }
              className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] !bg-white !text-[14px] !font-medium !leading-[20px] !text-[#2d2923] placeholder:!text-[rgba(45,41,35,0.72)] dark:!border-[rgba(255,255,255,0.12)] dark:!bg-[#0d0d0d] dark:!text-white dark:placeholder:!text-[rgba(255,255,255,0.72)]"
              required
            />
            {formData.confirmPassword && formData.password && (
              <div className="flex items-center gap-[6px]">
                {formData.password === formData.confirmPassword ? (
                  <>
                    <img src={checkCircleIcon} alt="" className="h-4 w-4" />
                    <span className="font-plus-jakarta text-[12px] font-semibold leading-[16px] text-[#3d8c05] dark:text-[#5fae26]">Passwords match</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={16} className="text-red-500" />
                    <span className="font-plus-jakarta text-[12px] font-semibold leading-[16px] text-red-500">Passwords do not match</span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex w-[354px] max-w-full items-center gap-3">
            <Checkbox
              id="accept-terms-mobile"
              checked={acceptTerms}
              onCheckedChange={(checked) => onAcceptTermsChange(checked as boolean)}
              className="h-5 w-5 rounded-none border-2 border-[rgba(45,41,35,0.32)] bg-[rgba(255,255,255,0.56)] data-[state=checked]:bg-[#2d2923] data-[state=checked]:border-[rgba(255,255,255,0.82)] dark:bg-[rgba(28,28,28,0.56)] dark:border-[rgba(253,253,253,0.32)] dark:data-[state=checked]:bg-[#ecebeb] dark:data-[state=checked]:border-[rgba(45,41,35,0.82)] dark:data-[state=checked]:[&>span>svg]:text-[#2d2923]"
            />
            <label
              htmlFor="accept-terms-mobile"
              className="cursor-pointer font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]"
            >
              I agree to the{' '}
              <Link to="/terms" className="underline decoration-solid text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="underline decoration-solid text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                Privacy Policy
              </Link>
            </label>
          </div>

          <div className="space-y-4 pt-4">
            <Button
              type="submit"
              variant="primary"
              className="h-12 w-full rounded-none bg-[rgba(45,41,35,0.72)] font-plus-jakarta text-[14px] font-medium leading-[20px] text-white hover:bg-[rgba(45,41,35,0.85)] hover:text-white dark:bg-[#ecebeb] dark:text-[#2d2923] dark:hover:bg-[#ecebeb] dark:hover:text-[#2d2923]"
              isLoading={loading}
            >
              CREATE ACCOUNT
            </Button>
            <p className="flex items-center justify-center gap-1 font-plus-jakarta text-[14px] font-normal leading-[20px]">
              <span className="text-[rgba(45,41,35,0.64)] dark:text-[rgba(255,255,255,0.64)]">Already have an account?</span>
              <Link to="/login" className="text-[#2d2923] underline decoration-solid dark:text-white">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
